import type { Account, Entry } from "@/api/types";
import { signForAccountKind } from "@/lib/format";

export interface LiveKpiDelta {
  /** Signed delta in whole CENTS — integer arithmetic, never a running float
   *  sum of dollars. See `deriveLiveDelta` for why this is safe at all. */
  deltaCents: number;
  /** How many entries produced this delta. Zero means "nothing to show yet",
   *  not "the farm made no money" — callers use this to fall back to the
   *  fixed opening figure rather than printing a confident zero. */
  count: number;
}

/**
 * Opening-balance-plus-delta, the honest half of Dashboard.tsx's DEMO_KPIS.
 *
 * WHY THIS IS DIFFERENT FROM "summing the entries feed" (which DEMO_KPIS's own
 * comment forbids, correctly): this never touches the SEEDED history. It only
 * sums entries that arrived after this dashboard tab opened — passed in as
 * `sessionEntries`, which Dashboard.tsx derives from `useEntries`'
 * `sessionArrivalIds`, itself seeded from "now" the same way the SSE stream's
 * own cursor is (see useEntries.ts). A farm's real cash position is not 14
 * demo rows added up; it IS the seeded opening figure plus whatever has
 * genuinely happened since a human started watching this screen — that second
 * part is exactly what this function computes and nothing more.
 *
 * INTEGER CENTS, not running float dollars: `Entry.amount` arrives as a
 * decimal (e.g. 412.90), and summing a handful of those as JS numbers is safe
 * in practice, but this codebase's own rule ("never add, subtract, or
 * otherwise compute with this client-side" — api/types.ts `Entry.amount`) is
 * about NOT treating float dollars as exact. Rounding every amount to an
 * integer cent before adding removes the ambiguity entirely rather than
 * hoping a handful of additions stays clean.
 *
 * SIGN, not KIND-agnostic: farms report cash-basis (Schedule F), so an
 * ordinary operating expense reduces cash-on-hand and net-farm-income by the
 * same amount in the same period — one delta legitimately drives both KPIs
 * (see Dashboard.tsx). The sign now CALLS `lib/format.ts#signForAccountKind`
 * rather than restating it. It used to be a second, independently written
 * `kind === "Income" ? 1 : -1` — the two copies happened to agree, but nothing
 * made them agree, and a fix applied to one would have left the other inverted
 * on a screen showing the same money. There is one sign rule in this client and
 * it mirrors one rule in EasyFarm.Domain (ef-001).
 *
 * Nothing in the current quick-entry flow produces an Income entry
 * (QuickEntry.tsx restricts its picker to Expense accounts), but this stays
 * correct if that ever changes, and an entry with no accountId at all (the
 * concurrent-visitor e2e fixture posts one) is treated as a debit, the same
 * default EntryRow.tsx already uses for display.
 */
export function deriveLiveDelta(
  sessionEntries: readonly Entry[],
  accountById: ReadonlyMap<string, Account>,
): LiveKpiDelta {
  let deltaCents = 0;
  let count = 0;
  for (const entry of sessionEntries) {
    /*
      AN UNCATEGORISED ENTRY IS NOT IN THE LEDGER, so it must not be in this delta either.

      It used to be. `entry.accountId ? … : undefined` fell through to `signForAccountKind`,
      which treats an unknown kind as an outflow, so a scan the model could not categorise
      moved the card immediately — and an uncategorised entry writes NO `entry_line` rows at
      all. MEASURED 2026-09-07 on the booth tenant: 302 categorised entries produced 604
      ledger lines, two apiece; 3 uncategorised produced **zero**.

      So the figure moved while you watched and the ledger never agreed. On the next load the
      baseline is recomputed from the ledger, the delta resets to nothing, and the card GOES
      BACKWARDS: measured −$8,200 before a refresh and −$7,700 after, with a visitor's $500
      silently gone. The wall accumulates all day and Christy reloads it.

      Skipping them makes the live figure equal what a reload will show, which is the whole
      claim the card makes — "From the ledger". The entry is not lost or hidden: it is in
      Recent activity, and the insight strip counts it as a receipt still to categorise, which
      is the honest story — filed, not yet classified, so not yet in your books.

      WHETHER IT SHOULD POST TO A SUSPENSE ACCOUNT INSTEAD is a real accounting question and
      a bigger change; it is queued for a human rather than decided here.
    */
    if (!entry.accountId) continue;
    const kind = accountById.get(entry.accountId)?.kind;
    const sign = signForAccountKind(kind) === "+" ? 1 : -1;
    deltaCents += sign * Math.round(entry.amount * 100);
    count += 1;
  }
  // COUNT MATCHES THE DELTA, not the arrivals. It drives the "+$12" flash chip, and a chip
  // announcing an arrival that moved nothing would print a confident $0.
  return { deltaCents, count };
}
